import React, { memo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import Carousel from 'src/components/Elements/Carousel';
import Preview from './Preview';
import Slide from './Slide';
import styles from './styles.scss';

const ImageGallery = ({ componentInsert = null, gallery = [], className = '' }) => {
  const [slideIndex, setSlideIndex] = useState(0);
  const carouselRef = useRef();

  const setCurrentSlide = (_, nextIndex) => {
    // we're using beforeChange because afterChange doesn't always trigger
    setSlideIndex(nextIndex);
  };

  const goToSlide = (i) => () => {
    carouselRef.current.slickGoTo(i);
  };

  if (!Array.isArray(gallery) || !gallery.length) return null;

  return (
      <div className={cn(styles.container, className)}>
        <Preview gallery={gallery} slideIndex={slideIndex} goToSlide={goToSlide} />
        <div className={styles.carouselWrapper}>
          <Carousel
            equalHeight
            initialSlide={0}
            ref={carouselRef}
            infinite={false}
            beforeChange={setCurrentSlide}
            resolutions={{
              992: {
                dots: true,
                infinite: true,
              },
            }}
          >
            {gallery.map((item, i) => (
              <Slide key={i} item={item} itemIndex={i} slideIndex={slideIndex} />
            ))}
          </Carousel>
          {componentInsert && componentInsert}
        </div>
      </div>
  );
};

ImageGallery.propTypes = {
  gallery: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.oneOf(['image', 'video']),
      source: PropTypes.string,
      thumbnailSource: PropTypes.string,
    }),
  ),
  className: PropTypes.string,
  componentInsert: PropTypes.node,
};

export default memo(ImageGallery);

/*
  ImageGallery

  A gallery to view images and videos.

  Videos can be self-hosted or externally-hosted. Only mp4 files are
  supported for self-hosted videos. Otherwise, update webpack as you see fit.
  For externally hosted videos, see react-player for a list of compatible
  services.

  Main Prop:
  gallery - Array - An array of images/videos to show.
  Each item should have these keys
    type - Should be 'image' or 'video'
    source - A link to the asset
    thumbnailSource - A link to the thumbnail asset (preview)

  --------------

  Usage

  import ImageGallery from 'src/components/Elements/ImageGallery';
  import anImage from './anImage.png';
  import aThumbnailImage from './aThumbnailImage.png';
  import videoPlaceholder from './videoPlaceholder.png';
  import aVideo from './aVideo.mp4';

  ...

  <ImageGallery
    gallery={[{
      type: 'image',
      source: anImage,
      thumbnailSource: aThumbnailImage,
    }, {
      type: 'video',
      source: 'https://youtu.be/RLWcYADoV84',
      thumbnailSource: videoPlaceholder,
    }, {
      type: 'video',
      source: aVideo,
      thumbnailSource: videoPlaceholder,
    }]}
  />

*/
